import { api } from '@/common/config';
import useUserCollaboration from '@/hooks/useUserCollaboration';
import { getAuthData } from '@/utils/auth';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

const SectionLock = () => {
  const { accessToken, username } = getAuthData();
  const sectionId = useSelector((state: any) => state.sectionId.sectionId);
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [lockedOwner, setLockedOwner] = useState('');
  const [action, setAction] = useState('');
  const [isEditingSection, setIsEditingSection] = useState(false);
  const [showCollaborationPopup, setShowCollaborationPopup] = useState(false);

  const isLockedByOther = lockedOwner && lockedOwner !== username;
  const isLockedByMe = lockedOwner === username;
  const {
    requestLock,
    releaseLock,
    getSectionLockStatus,
    sectionLocks,
    isConnected,
    sectionUpdates,
    checkLockStatus,
  } = useUserCollaboration(
    `${api}/collab`, // your socket.io server URL
    accessToken,
    true // auto-connect on component mount
  );

  useEffect(() => {
    if (sectionId) {
      const currentLockOwner = getSectionLockStatus(sectionId);
      if (currentLockOwner && currentLockOwner !== username) {
        setLockedOwner(currentLockOwner);
        setAction('acquire');
      } else if (currentLockOwner == null) {
        setLockedOwner('');
      }
    }
  }, [sectionId, getSectionLockStatus, username, sectionLocks]);

  // Handler to edit a section with locking
  const handleEdit = async (sectionId: string) => {
    try {
      // Check if section is already locked by someone else first

      setIsEditingSection(true);
      const currentLockOwner = getSectionLockStatus(sectionId);

      if (currentLockOwner && currentLockOwner !== username) {
        // Section is locked by another user, show popup
        setLockedOwner(currentLockOwner);
        setShowCollaborationPopup(true);
        return;
      }

      // If not locked or locked by me, try to acquire lock
      const response = await requestLock(sectionId);

      if (response.status === 'success') {
        setLockedOwner(response?.lockedBy ?? '');
        setAction(response?.action);
        setActiveSection(sectionId);
      } else if (response.lockedBy && response.lockedBy !== username) {
        // Lock acquisition failed because someone else got the lock
        setLockedOwner(response.lockedBy);
        setShowCollaborationPopup(true);
      } else {
        alert(`Could not acquire lock: ${response.reason}`);
      }
    } catch (err) {
      console.error('Error requesting lock:', err);
    }
  };

  // Release lock for a section
  const releaseSectionLock = async (sectionId: string) => {
    // Only attempt to release if I'm the owner
    try {
      const response = await releaseLock(sectionId);
      if (response.action === 'release') {
        setLockedOwner('');
        setAction(response?.action);
        setActiveSection(null);
        return { success: true };
      } else {
        return { success: false, message: response.reason || 'Failed to release lock' };
      }
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error releasing lock',
      };
    }
  };

  // Handle global click to release lock if clicking outside editable area
  const handleGlobalClick = () => {
    setIsEditingSection(false);
    // Only process if there's an active section and I own the lock
    if (activeSection && isLockedByMe && !isEditingSection) {
      releaseSectionLock(activeSection);
    }
  };
  return {
    isLockedByOther,
    handleEdit,
    showCollaborationPopup,
    handleGlobalClick,
    activeSection,
    lockedOwner,
    action,
    releaseSectionLock,
    setShowCollaborationPopup,
    isLockedByMe,
    setIsEditingSection,
    sectionUpdates,
    isConnected,
    checkLockStatus,
  };
};

export default SectionLock;
